// =============================================================================
// ChatLayout — three columns. Sidebar (Teams / DMs / Unit threads / Archive)
// + Thread view + optional right rail. Mobile collapses to single column.
// =============================================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useThreadList, useThread, useMembers, useScopeMeta, useUnreadMap } from "../hooks.js";
import { sendMessage, openDm, markRead, uploadPhoto, uploadVoice } from "../send.js";
import { MessageBubble } from "./MessageBubble.jsx";
import { Composer } from "./Composer.jsx";
import { SevricMark } from "./SevricMark.jsx";
import { ArchiveModal } from "./ArchiveModal.jsx";
import { ComposeModal } from "./ComposeModal.jsx";
import { MobileNav } from "./MobileNav.jsx";

const TEAM_ICON = {
  people: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  wrench: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  sun:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  home:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  star:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
};

const ACCENT_BG = {
  cobalt:   { background: "var(--accent-soft, rgba(46,86,148,0.10))", color: "var(--accent, #2E5694)" },
  amber:    { background: "rgba(176,94,19,0.10)", color: "#B05E13" },
  green:    { background: "rgba(42,109,56,0.10)", color: "#2A6D38" },
  gold:     { background: "rgba(148,106,18,0.12)", color: "#946A12" },
  burgundy: { background: "rgba(124,41,53,0.10)", color: "#7C2935" },
};

export function ChatLayout({ profile }) {
  const uid = profile?.uid;
  const tenantId = profile?.tenantId;
  const author = useMemo(() => ({
    uid,
    displayName: profile?.displayName || profile?.name,
    initials: profile?.initials,
  }), [profile?.uid, profile?.displayName, profile?.name, profile?.initials]);

  const { channels, dms, entityThreads, loading: listLoading } = useThreadList({ uid, tenantId });
  const unread = useUnreadMap({ uid, channels, dms, entityThreads });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  // Default scope: DM with SEVRIC AI. We open-or-create on mount.
  const [scope, setScope] = useState(null);
  useEffect(() => {
    if (!uid || !tenantId || scope) return;
    let cancelled = false;
    (async () => {
      try {
        const dm = await openDm({ tenantId, callerUid: uid, otherUid: "sevric-ai" });
        if (!cancelled) setScope({ kind: "dm", id: dm.id, _meta: { isAi: true } });
      } catch {
        // Fall back to the first DM that exists.
        if (!cancelled && dms[0]) setScope({ kind: "dm", id: dms[0].id });
      }
    })();
    return () => { cancelled = true; };
  }, [uid, tenantId, scope, dms]);

  // Mark scope as read when opened.
  useEffect(() => {
    if (uid && scope?.id) markRead({ uid, scope }).catch(() => {});
  }, [uid, scope?.id, scope?.kind]);

  return (
    <main className="chat-app">
      <ChatSidebar
        profile={profile}
        channels={channels}
        dms={dms}
        entityThreads={entityThreads}
        loading={listLoading}
        activeScope={scope}
        unread={unread}
        onSelect={setScope}
        onOpenArchive={() => setArchiveOpen(true)}
        onOpenCompose={() => setComposeOpen(true)}
      />
      <ChatThread
        scope={scope}
        author={author}
        tenantId={tenantId}
        profile={profile}
      />
      <ChatRightRail scope={scope} tenantId={tenantId} callerUid={uid} />
      {archiveOpen && (
        <ArchiveModal
          tenantId={tenantId}
          onClose={() => setArchiveOpen(false)}
          onOpen={(et) => {
            setArchiveOpen(false);
            setScope({ kind: "entity_thread", id: et.id });
          }}
        />
      )}
      {composeOpen && (
        <ComposeModal
          profile={profile}
          onClose={() => setComposeOpen(false)}
          onOpenScope={(s) => setScope(s)}
        />
      )}
      {/* Mobile bottom nav — only renders under 720px via CSS */}
      <MobileNav
        profile={profile}
        channels={channels}
        dms={dms}
        entityThreads={entityThreads}
        unread={unread}
        onSelect={setScope}
        onOpenAi={async () => {
          try {
            const dm = await openDm({ tenantId: profile.tenantId, callerUid: profile.uid, otherUid: "sevric-ai" });
            setScope({ kind: "dm", id: dm.id, _meta: { isAi: true } });
          } catch {}
        }}
        onOpenCompose={() => setComposeOpen(true)}
        onOpenArchive={() => setArchiveOpen(true)}
      />
    </main>
  );
}

// =============================================================================
// Sidebar
// =============================================================================
function ChatSidebar({ profile, channels, dms, entityThreads, loading, activeScope, unread, onSelect, onOpenArchive, onOpenCompose }) {
  const greeting = useMemo(() => greetingFor(profile), [profile?.uid]);
  return (
    <aside className="chat-sidebar">
      <div className="chat-sb-head">
        <div className="chat-sb-eye"><span className="dot" />MESSAGES</div>
        <button type="button" className="chat-sb-compose" onClick={onOpenCompose}>+ Compose</button>
      </div>
      <div className="chat-sb-greet">
        {greeting}, <em>{profile?.displayName?.split(" ")[0] || profile?.name?.split(" ")[0] || "there"}</em>.
      </div>

      {/* SEVRIC AI section */}
      <div className="chat-sb-section">
        <div className="chat-sb-section-h cv"><span>SEVRIC AI</span></div>
        <SidebarAiRow active={activeScope?._meta?.isAi} onClick={async () => {
          try {
            const dm = await openDm({ tenantId: profile.tenantId, callerUid: profile.uid, otherUid: "sevric-ai" });
            onSelect({ kind: "dm", id: dm.id, _meta: { isAi: true } });
          } catch {}
        }} />
      </div>

      {/* Teams */}
      <div className="chat-sb-section">
        <div className="chat-sb-section-h cv"><span>Teams</span><span className="count">+ new</span></div>
        {loading && <div className="chat-sb-loading">Loading teams…</div>}
        {!loading && channels.length === 0 && <div className="chat-sb-empty">No teams yet.</div>}
        {channels.map((ch) => (
          <SidebarTeamRow
            key={ch.id}
            channel={ch}
            active={activeScope?.kind === "channel" && activeScope?.id === ch.id}
            unread={unread?.[`channel:${ch.id}`]}
            onClick={() => onSelect({ kind: "channel", id: ch.id })}
          />
        ))}
      </div>

      {/* Direct Messages */}
      <div className="chat-sb-section">
        <div className="chat-sb-section-h cv"><span>Direct Messages</span></div>
        {dms.filter((d) => !d.is_ai).map((dm) => (
          <SidebarDmRow
            key={dm.id}
            dm={dm}
            callerUid={profile.uid}
            active={activeScope?.kind === "dm" && activeScope?.id === dm.id && !activeScope?._meta?.isAi}
            unread={unread?.[`dm:${dm.id}`]}
            onClick={() => onSelect({ kind: "dm", id: dm.id })}
          />
        ))}
      </div>

      {/* Unit threads */}
      <div className="chat-sb-section">
        <div className="chat-sb-section-h cv"><span>Unit threads</span></div>
        {entityThreads.map((et) => (
          <SidebarEntityRow
            key={et.id}
            thread={et}
            active={activeScope?.kind === "entity_thread" && activeScope?.id === et.id}
            unread={unread?.[`entity_thread:${et.id}`]}
            onClick={() => onSelect({ kind: "entity_thread", id: et.id })}
          />
        ))}
      </div>

      <div className="chat-sb-archive" onClick={onOpenArchive}>
        <span>📦 Past units · archived</span>
        <span style={{ color: "var(--accent, #2E5694)" }}>→</span>
      </div>
    </aside>
  );
}

function SidebarAiRow({ active, onClick }) {
  return (
    <div className={`chat-sb-item${active ? " active" : ""}`} onClick={onClick}>
      <div className="chat-sb-avi ai">
        <SevricMark size={32} />
        <span className="presence" />
      </div>
      <div className="chat-sb-text">
        <div className="chat-sb-name">
          SEVRIC AI
          <span className="chat-sb-ai-mark">AI</span>
        </div>
        <div className="chat-sb-sub">online · ready to help</div>
      </div>
    </div>
  );
}

function SidebarTeamRow({ channel, active, unread, onClick }) {
  const icon = TEAM_ICON[channel.icon] || TEAM_ICON.people;
  const accent = ACCENT_BG[channel.accent] || ACCENT_BG.cobalt;
  return (
    <div className={`chat-sb-item${active ? " active" : ""}`} onClick={onClick}>
      <div className="chat-sb-avi unit" style={accent}>{icon}</div>
      <div className="chat-sb-text">
        <div className="chat-sb-name">{channel.name}</div>
        <div className="chat-sb-sub">{channel.description || ""}</div>
      </div>
      {unread && <span className="chat-sb-unread-dot" aria-label="unread" />}
    </div>
  );
}

function SidebarDmRow({ dm, callerUid, active, unread, onClick }) {
  // Pick the side of the pair that's NOT the caller. `a` and `b` are
  // joined sevric_users rows from the hook so we get real display name.
  const other = dm.participant_a === callerUid ? dm.b : dm.a;
  const name = other?.display_name || other?.uid || "Someone";
  const initials = (other?.initials || name.slice(0, 2)).toUpperCase();
  const presenceClass = (other?.presence && other.presence !== "online") ? " off" : "";
  return (
    <div className={`chat-sb-item${active ? " active" : ""}`} onClick={onClick}>
      <div className="chat-sb-avi h2">
        {initials}
        <span className={`presence${presenceClass}`} />
      </div>
      <div className="chat-sb-text">
        <div className="chat-sb-name">{name}</div>
        <div className="chat-sb-sub">{other?.presence_label || "DM"}</div>
      </div>
      {unread && <span className="chat-sb-unread-dot" />}
    </div>
  );
}

const ENTITY_ICON = {
  vehicle: (
    <svg viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="14">
      <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" />
      <circle cx="12" cy="16" r="2" /><circle cx="30" cy="16" r="2" />
    </svg>
  ),
  battery_unit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <rect x="2" y="7" width="16" height="10" rx="2" /><line x1="22" y1="11" x2="22" y2="13" /><line x1="6" y1="11" x2="6" y2="13" /><line x1="10" y1="11" x2="10" y2="13" />
    </svg>
  ),
  vendor: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
};

function SidebarEntityRow({ thread, active, unread, onClick }) {
  const icon = ENTITY_ICON[thread.entity_kind] || ENTITY_ICON.vehicle;
  return (
    <div className={`chat-sb-item${active ? " active" : ""}`} onClick={onClick}>
      <div className="chat-sb-avi unit">{icon}</div>
      <div className="chat-sb-text">
        <div className="chat-sb-name">{thread.entity_label || thread.entity_id}</div>
        <div className="chat-sb-sub">{thread.entity_kind.replace("_", " ")}</div>
      </div>
      {unread && <span className="chat-sb-unread-dot" />}
    </div>
  );
}

// =============================================================================
// Thread view
// =============================================================================
function ChatThread({ scope, author, tenantId, profile }) {
  const { messages, loading } = useThread({ scope });
  const meta = useScopeMeta({ scope, callerUid: profile?.uid });
  // Photo input ref for the "Attach photo" button
  const photoInputRef = useRef(null);
  const voiceRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);

  if (!scope) {
    return (
      <section className="chat-thread">
        <div className="chat-empty">
          <h2 className="chat-empty-h1">Pick a <em>conversation</em>.</h2>
          <p className="chat-empty-sub">Or talk to SEVRIC AI — it knows your floor.</p>
        </div>
      </section>
    );
  }

  async function handlePhotoFile(file) {
    if (!file || !scope?.id) return;
    // Insert the message first so we have a stable message_id for the storage path.
    const messageId = crypto.randomUUID();
    const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
    const filename = `${messageId}.${ext}`;
    const uploaded = await uploadPhoto({ tenantId, scope, messageId, file: new File([file], filename, { type: file.type }) });
    const { data, error } = await import("../client.js").then((m) => m.getSupabase().storage.from("chat-photos").createSignedUrl(uploaded.path, 60 * 60 * 24 * 7));
    const url = data?.signedUrl;
    await sendMessage({
      scope, author, tenantId,
      body: null,
      messageKind: "photo",
      attachments: [{ type: "photo", url, name: file.name, size: file.size, mime: file.type, path: uploaded.path }],
    });
  }

  async function handleAttach(kind) {
    if (kind === "photo" && photoInputRef.current) {
      photoInputRef.current.click();
    } else if (kind === "voice") {
      // Toggle MediaRecorder
      if (recording && voiceRecorderRef.current) {
        voiceRecorderRef.current.stop();
        setRecording(false);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
        voiceRecorderRef.current = recorder;
        const chunks = [];
        recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
        recorder.onstop = async () => {
          const blob = new Blob(chunks, { type: "audio/webm" });
          const messageId = crypto.randomUUID();
          const filename = `${messageId}.webm`;
          const uploaded = await uploadVoice({ tenantId, scope, messageId, file: new File([blob], filename, { type: "audio/webm" }) });
          const { data } = await import("../client.js").then((m) => m.getSupabase().storage.from("chat-voice").createSignedUrl(uploaded.path, 60 * 60 * 24 * 7));
          await sendMessage({
            scope, author, tenantId,
            body: null,
            messageKind: "voice",
            attachments: [{ type: "voice", url: data?.signedUrl, duration_ms: null, mime: "audio/webm", path: uploaded.path }],
          });
          stream.getTracks().forEach((t) => t.stop());
        };
        recorder.start();
        setRecording(true);
      } catch (err) {
        // Permission denied or unsupported — surface gracefully.
        // eslint-disable-next-line no-console
        console.warn("Voice record failed:", err?.message);
      }
    }
  }

  return (
    <section className="chat-thread">
      <div className="chat-thread-head">
        <ThreadHeader scope={scope} meta={meta.data} loading={meta.loading} />
        {scope.kind === "entity_thread" && meta.data && <EntityStrip et={meta.data} />}
      </div>
      <div className="chat-messages">
        {loading && <div className="chat-loading">Loading the thread…</div>}
        {!loading && messages.length === 0 && (
          <div className="chat-empty-thread">
            <h3 className="chat-empty-h2">Quiet here. <em>Say something.</em></h3>
          </div>
        )}
        {messages.map((m) => <MessageBubble key={m.id} msg={m} />)}
      </div>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoFile(f); e.target.value = ""; }}
      />
      <Composer
        placeholder={composerPlaceholderFor(scope, meta.data)}
        hint={composerHintFor(scope, recording)}
        onSend={async ({ body, mentions }) => {
          await sendMessage({ scope, author, tenantId, body, mentions });
        }}
        onAttach={handleAttach}
      />
    </section>
  );
}

function ThreadHeader({ scope, meta, loading }) {
  if (scope._meta?.isAi || (scope.kind === "dm" && meta?.kind === "ai")) {
    return (
      <div className="chat-thread-top">
        <div className="chat-thread-avi ai">
          <SevricMark size={44} withGlow />
          <span className="presence" />
        </div>
        <div className="chat-thread-title">
          <h2>SEVRIC AI <span className="badge">AI · ALWAYS ON</span></h2>
          <div className="chat-thread-meta"><span className="ok-dot" /><span>online · ready · remembers everything you share</span></div>
        </div>
      </div>
    );
  }
  if (scope.kind === "channel") {
    const accent = ACCENT_BG[meta?.accent] || ACCENT_BG.cobalt;
    const icon = TEAM_ICON[meta?.icon] || TEAM_ICON.people;
    return (
      <div className="chat-thread-top">
        <div className="chat-thread-avi" style={{ background: accent.background, color: accent.color }}>{icon}</div>
        <div className="chat-thread-title">
          <h2>{loading ? "…" : (meta?.name || "Team")} <span className="badge">TEAM · {meta?.memberCount ?? 0} TEAMMATES</span></h2>
          <div className="chat-thread-meta"><span className="ok-dot" /><span>{meta?.description || "open"}</span></div>
        </div>
      </div>
    );
  }
  if (scope.kind === "entity_thread") {
    return (
      <div className="chat-thread-top">
        <div className="chat-thread-avi" style={{ background: "var(--accent-soft, rgba(46,86,148,0.10))", color: "var(--accent, #2E5694)" }}>
          {ENTITY_ICON[meta?.entity_kind] || ENTITY_ICON.vehicle}
        </div>
        <div className="chat-thread-title">
          <h2>{loading ? "…" : (meta?.entity_label || "Unit thread")} <span className="badge">UNIT THREAD</span></h2>
          <div className="chat-thread-meta">
            <span className="ok-dot" />
            <span>{meta?.status === "archived" ? "Archived · " + (meta?.archived_reason || "—") : "Active · auto-archives when sold"}</span>
          </div>
        </div>
      </div>
    );
  }
  // Human DM
  const dn = meta?.displayName || "Direct";
  const initials = meta?.initials || dn.slice(0, 2).toUpperCase();
  return (
    <div className="chat-thread-top">
      <div className="chat-thread-avi" style={{ background: "linear-gradient(135deg, #2A6D38, #4A7BC8)", color: "#FBFBF9" }}>
        {initials}
        <span className="presence" />
      </div>
      <div className="chat-thread-title">
        <h2>{dn}</h2>
        <div className="chat-thread-meta"><span className="ok-dot" /><span>{meta?.presenceLabel || meta?.presence || "—"}</span></div>
      </div>
    </div>
  );
}

function EntityStrip({ et }) {
  return (
    <div className="chat-entity-strip">
      <div className="chat-entity-icon">{ENTITY_ICON[et.entity_kind] || ENTITY_ICON.vehicle}</div>
      <div className="chat-entity-text">
        <div className="chat-entity-name">{et.entity_label || et.entity_id}</div>
        <div className="chat-entity-sub">
          <span>{(et.entity_kind || "").replace("_", " ")}</span>
          {et.status === "archived" && <span className="chat-entity-tag burgundy">Archived</span>}
          {et.status === "active" && <span className="chat-entity-tag green">Active</span>}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Right rail
// =============================================================================
function ChatRightRail({ scope, tenantId, callerUid }) {
  const { members, loading } = useMembers({ scope, tenantId });
  return (
    <aside className="chat-rightpanel">
      <div className="chat-rp-eye"><span>In this conversation</span><span style={{ color: "var(--accent, #2E5694)" }}>{members.length}</span></div>
      {loading && <div className="chat-rp-loading">Loading…</div>}
      {members.map((m) => (
        <div key={m.uid} className="chat-rp-member">
          <div className={`chat-rp-mavi${m.uid === "sevric-ai" ? " ai" : ""}`}>
            {m.uid === "sevric-ai" ? <SevricMark size={28} /> : (m.initials || m.display_name?.slice(0, 2) || "??").toUpperCase()}
            <span className={`pres${m.presence !== "online" ? " off" : ""}`} />
          </div>
          <div>
            <div className="chat-rp-mname">{m.uid === "sevric-ai" ? "SEVRIC AI" : (m.display_name || m.uid)}</div>
            <div className="chat-rp-msub">{m.presence_label || m.presence || "—"}</div>
          </div>
        </div>
      ))}
    </aside>
  );
}

// =============================================================================
// Helpers
// =============================================================================
function greetingFor(profile) {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
function composerPlaceholderFor(scope, meta) {
  if (!scope) return "Message…";
  if (scope._meta?.isAi || meta?.kind === "ai") return "Message SEVRIC AI…";
  if (scope.kind === "channel") return `Message ${meta?.name || "the team"}…`;
  if (scope.kind === "entity_thread") return "Add to the thread…";
  if (scope.kind === "dm" && meta?.displayName) return `Message ${meta.displayName}…`;
  return "Message…";
}

function composerHintFor(scope, recording) {
  if (recording) return "● Recording — tap mic to stop and send";
  if (scope?.kind === "entity_thread") return "This thread archives automatically when the unit is sold.";
  return undefined;
}
